<template>
	<div class="map-sticky bg-light border-bottom mx-n2 p-2 mx-md-0 px-md-0 z-1">
		<div class="map-controls d-flex gap-1 gap-md-4">
			<div class="form-floating flex-grow-1">
				<input
					id="floatingInput"
					ref="locationInput"
					type="search"
					class="form-control"
					placeholder="Search"
					data-testid="find-location-search-input"
				/>
				<label for="floatingInput">Search</label>
			</div>

			<button
				type="button"
				class="btn btn-outline-dark btn-icon px-3 px-md-5"
				data-bs-toggle="collapse"
				data-bs-target="#filterPanel"
				aria-expanded="false"
				aria-controls="filterPanel"
			>
				<FilterIcon :width="12" :height="12" />
				<span class="visually-hidden">
					Filters, <span>{{ filtersCountDisplay }}</span> currently applied
				</span>
				<span class="d-none d-md-inline">Filters</span>
				<span v-if="hasFilters">{{ filtersCountDisplay }}</span>
			</button>

			<!-- TODO: Reverse engineer what this error text does and implement
				<span class="label-help-error hide"></span>
				--></div>

		<LocationFilters
			:selected-filters="selectedFilters"
			@filter-change="$emit('filter-change', $event)"
			@filter-clear="$emit('filter-clear')"
		/>

		<div class="d-flex justify-content-between mt-3">
			<div class="caption">
				<span v-if="zeroResults">No location found for search text "{{ locationString }}".</span>
				<span v-else class="mb-1">
					<span class="font-weight-bold">{{ matchingLocations.length }}</span>
					{{ resultText }}
				</span>
			</div>
			<a class="d-none d-lg-inline" :href="viewAllClubsUrl"> View All Locations </a>
		</div>
		<div class="d-flex d-lg-none justify-content-between mt-2">
			<a :href="viewAllClubsUrl"> View All Locations </a>
			<a :href="comingSoonClubsUrl"> Coming Soon Clubs</a>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount } from 'vue';
import { loader } from 'Lib/components/find-a-location/util/maps-loader';
import LocationFilters from 'Lib/components/find-a-location/LocationFilters.vue';
import FilterIcon from 'Lib/icons/FilterIcon.vue';
import type {
	Location,
	IFilterSelection,
	IFilterChangePayload
} from 'Types/aemui/find-a-location/findaLocation';
import { getGlobalPageDriverUrl } from '@/util/globalPageDriver';
import { logError, logWarn } from '@/util/logger';

const props = defineProps<{
	matchingLocations: Location[];
	selectedFilters: IFilterSelection[];
}>();

const emit = defineEmits<{
	(e: 'set-search-position', latLng: google.maps.LatLngLiteral): void;
	(e: 'filter-change', payload: IFilterChangePayload): void;
	(e: 'filter-clear'): void;
}>();

const comingSoonClubsUrl = getGlobalPageDriverUrl('comingSoonClubs');
const viewAllClubsUrl = getGlobalPageDriverUrl('viewAllClubs');

const locationString = ref('your location'); // the string they submitted to search
const zeroResults = ref(false); // true if their most-recent search text returned no results
const isSearching = ref(false); // whether we are doing HTTP for search
const locationInput = ref<HTMLInputElement | null>(null);

let geocoder: google.maps.Geocoder | null = null;
let autoComplete: google.maps.places.Autocomplete | null = null;

onBeforeMount(async () => {
	try {
		const { Geocoder } = (await loader.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
		geocoder = new Geocoder();
		await initAutoComplete();
	} catch (error) {
		logError('Error initializing location search', {}, error);
	}
});

const hasFilters = computed(() => {
	return Boolean(props.selectedFilters.length);
});

const filtersCount = computed(() => {
	if (!hasFilters.value) return 0;
	return props.selectedFilters.length;
});

const filtersCountDisplay = computed(() => {
	if (!hasFilters.value) return '';
	return `(${filtersCount.value})`;
});

const resultText = computed(() => {
	let text = props.matchingLocations.length === 1 ? 'result' : 'results';

	if (hasFilters.value) {
		text += ` matching your ${filtersCount.value === 1 ? 'filter' : 'filters'}`;
	}
	text += ` near ${locationString.value}.`;

	return text;
});

const onSearch = async (searchString: string) => {
	try {
		isSearching.value = true;
		zeroResults.value = false;

		// Add their search to the URL so if they go to a club details page
		// then click the browser BACK button, they'll be back to their search.
		const url = new URL(window.location.href);
		url.searchParams.set('query', searchString);
		window.history.replaceState(null, '', url);

		const response = await geocoder?.geocode({ address: searchString });
		const firstResult = response?.results?.[0];
		if (typeof firstResult?.geometry?.location?.lat === 'function') {
			const lat = firstResult.geometry.location.lat();
			const lng = firstResult.geometry.location.lng();
			emit('set-search-position', { lat, lng });
			locationString.value = firstResult.formatted_address;
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error?.code === google.maps.GeocoderStatus.ZERO_RESULTS) {
			zeroResults.value = true;
			locationString.value = searchString;
		} else {
			logWarn('Error searching for location', { searchString }, error);
		}
	} finally {
		isSearching.value = false;
	}
};

const initAutoComplete = async () => {
	const options = {
		types: ['geocode']
	};
	const input = locationInput.value as HTMLInputElement;

	// If there is a URL query parameter named `query`
	// make it so search starts there to enable deep links like `?query=boston`
	const url = new URL(window.location.href);
	const query = url.searchParams.get('query');
	if (query) {
		input.value = query;
		onSearch(input.value);
	}

	const { Autocomplete } = (await loader.importLibrary('places')) as google.maps.PlacesLibrary;
	autoComplete = new Autocomplete(input, options);

	autoComplete?.addListener('place_changed', () => {
		onSearch(input.value);
	});
};
</script>
